// src/prototype/pages/EAActivatePage.tsx
// ============================================================
// EA STEP 3: EXECUTION PORTFOLIO
// Route: /activate
//
// Entry point to the Execute path: the initiatives you proceeded with in Evaluate,
// each a card that opens its Initiative Command Center. Champion and initiate them
// through approval, activation, and delivery.
//
// Replaces the former six-section approval playbook. The approval artifacts it carried
// (CFO Summary, Board Rationale, Executive Brief, Assumptions Appendix) now open from
// the command center's CFO panel, beside the financial summary that justifies them.
// ============================================================

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Rocket } from 'lucide-react';

import { EAJourneyBarFrame } from '../components/EAJourneyBarFrame';
import { ExecStatusPill } from '../components/ExecStatusPill';
import { ApprovalChain } from '../components/ApprovalChain';

import { MOCK_OPPORTUNITIES } from '../data/mockOpportunities';
import { getDecisionBrief, type DecisionBrief } from '../data/decisionBrief';
import { getInitiativeRecord, type InitiativeRecord } from '../data/executionInitiatives';
import { useEA } from '../contexts/EAContext';
import { useEntitlements } from '../hooks/useEntitlements';
import { getOpportunityRank } from '../utils/opportunityRanking';
import { useExecutePortfolio } from '../hooks/useExecutePortfolio';


interface PortfolioEntry {
  brief: DecisionBrief;
  record: InitiativeRecord;
}

export default function EAActivatePage() {
  const navigate = useNavigate();
  const { categoryWeights } = useEA();
  const { can } = useEntitlements();
  const { portfolio } = useExecutePortfolio();

  // The entire Execute section is Pro.
  useEffect(() => {
    if (!can.execute) navigate('/upgrade');
  }, [can.execute, navigate]);

  const entries: PortfolioEntry[] = useMemo(
    () =>
      portfolio
        .map((id) => {
          const opportunity = MOCK_OPPORTUNITIES.find((o) => o.id === id);
          if (!opportunity) return null;
          const brief = getDecisionBrief(opportunity, getOpportunityRank(id, categoryWeights));
          return { brief, record: getInitiativeRecord(id, brief) };
        })
        .filter((entry): entry is PortfolioEntry => entry !== null),
    [portfolio, categoryWeights],
  );

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Journey bar — locked existing UX. */}
      <EAJourneyBarFrame />

      <main className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
          <div>
            <h1 className="text-lg font-bold text-[#18365E]">Execution Portfolio</h1>
            <p className="text-xs text-[#717182] mt-0.5">
              {entries.length} approved initiative{entries.length !== 1 ? 's' : ''} · champion and initiate each through
              approval, activation, and delivery
            </p>
          </div>
          <button
            onClick={() => navigate('/opportunities')}
            className="text-xs font-semibold text-[#18365E] border border-[#e5e7eb] rounded-lg px-3 py-2 hover:border-[#18365E] flex items-center gap-1.5 transition-colors bg-white"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Evaluate
          </button>
        </div>

        {entries.length === 0 ? (
          <div className="bg-white rounded-xl border border-[#e5e7eb] px-6 py-14 text-center">
            <Rocket className="w-10 h-10 text-[#e5e7eb] mx-auto mb-3" />
            <p className="text-sm font-bold text-[#18365E]">No initiatives yet</p>
            <p className="text-xs text-[#717182] mt-1 max-w-md mx-auto leading-relaxed">
              Initiatives enter this portfolio when you proceed with them in Evaluate. Open a decision brief and take the
              action its verdict recommends.
            </p>
            <button
              onClick={() => navigate('/opportunities')}
              className="mt-5 bg-[#FF6E4C] text-white text-xs font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5 hover:bg-[#e85f3f] transition-colors"
            >
              Go to Evaluate
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="grid gap-3">
            {entries.map(({ brief, record }) => {
              const done = record.milestones.filter((m) => m.status === 'Complete').length;
              const next = record.milestones.find((m) => m.status !== 'Complete');
              const gate = record.approval.find((s) => s.state === 'current');

              return (
                <div
                  key={record.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/execute/${record.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/execute/${record.id}`);
                    }
                  }}
                  className="bg-white rounded-xl border border-[#e5e7eb] p-5 cursor-pointer hover:border-[#18365E]/40 hover:shadow-sm transition-all group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6E4C]/50"
                >
                  <div className="flex items-start gap-4 flex-wrap">
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-2.5 flex-wrap mb-1">
                        <h3 className="text-sm font-bold text-[#18365E] group-hover:text-[#FF6E4C] transition-colors">
                          {brief.title}
                        </h3>
                        <ExecStatusPill status={record.status} />
                      </div>
                      <p className="text-[11px] text-[#717182]">
                        Sponsor: <b className="text-[#4b5563]">{record.sponsor}</b> · Owner:{' '}
                        <b className="text-[#4b5563]">{record.owner}</b> · {record.phase}
                      </p>
                      <div className="flex items-center gap-3 mt-3 flex-wrap">
                        <ApprovalChain steps={record.approval} compact />
                        <span className="text-[10px] text-[#717182]">
                          {gate ? (
                            <span className="font-semibold text-[#FF6E4C]">{gate.label} — in progress</span>
                          ) : (
                            'Fully approved'
                          )}
                        </span>
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <span className="text-[10px] text-[#717182] block">Annual ROI</span>
                      <span className="text-base font-bold text-[#18365E]">{brief.roiShort}</span>
                      <span className="text-[10px] text-[#717182] block mt-1">
                        {done}/{record.milestones.length} milestones · next: {next ? next.phase : '—'}
                      </span>
                    </div>

                    <span className="shrink-0 self-center inline-flex items-center gap-1 text-xs font-semibold text-[#18365E] group-hover:text-[#FF6E4C] transition-colors">
                      Command center
                      <ChevronRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-[11px] text-[#717182] mt-4">
          Initiatives enter this portfolio when you proceed with them in <b>Evaluate</b>. The evaluation brief stays
          linked from every command center.
        </p>
      </main>
    </div>
  );
}
